import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from "react";
import * as XLSX from "xlsx";
import "./tabla.scss";
import "./tabs.scss";

// Genera un id seguro para DOM
function sanitizeKey(key) {
  return String(key).replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "");
}

// Normaliza valores: quita $, MXN/USD, %, espacios, separadores de miles; maneja negativos con paréntesis
function normalize(val) {
  if (val === null || val === undefined) return "";
  let s = String(val).trim();
  let neg = false;
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1); }
  s = s
    .replace(/\s?(MXN|USD)\s*$/i, "")
    .replace(/^-\s*\$\s?/, "-") // -$1,234.56
    .replace(/^\$\s?/, "") // $1,234.56
    .replace(/\s/g, "")
    .replace(/%$/, "");
  s = s.replace(/,/g, "");
  if (neg) s = "-" + s;
  return s;
}

const DatosTabla = forwardRef(({ data, tableId = "tbl", ids }, ref) => {
  const analyticsData = useMemo(() => data || {}, [data]);
  const analyticsIds = useMemo(() => ids || {}, [ids]);
  const Productos = useMemo(() => Object.keys(analyticsData), [analyticsData]);
  const idsProductos = useMemo(() => Object.keys(analyticsIds), [analyticsIds]);

  const [tabIdActivo, setTabIdActivo] = useState(null);
  const [tabCategoriaActiva, setTabCategoriaActiva] = useState(null);
  const [celdaSeleccionada, setCeldaSeleccionada] = useState({ fila: null, col: null });

  // Inicializa primer producto/categoría
  useEffect(() => {
    if (Productos.length > 0) {
      const firstId = Productos[0];
      setTabIdActivo(firstId);
      const categorias = Object.keys(analyticsData[firstId] || {});
      setTabCategoriaActiva(categorias.length > 0 ? categorias[0] : null);
    } else {
      setTabIdActivo(null);
      setTabCategoriaActiva(null);
    }
  }, [analyticsData, Productos]);

  // Cuando cambia el producto, actualizar categoría
  useEffect(() => {
    if (tabIdActivo) {
      const categorias = Object.keys(analyticsData[tabIdActivo] || {});
      if (!categorias.includes(tabCategoriaActiva)) {
        setTabCategoriaActiva(categorias.length > 0 ? categorias[0] : null);
      }
    }
  }, [tabIdActivo, analyticsData, tabCategoriaActiva]);

  const handleClickCelda = (filaIndex, colIndex) => {
    if (celdaSeleccionada.fila === filaIndex && celdaSeleccionada.col === colIndex) {
      setCeldaSeleccionada({ fila: null, col: null });
    } else {
      setCeldaSeleccionada({ fila: filaIndex, col: colIndex });
    }
  };

  // Busca la primera ocurrencia del valor en TODA la data de ESTA tabla
  const findLocationByValue = (rawValue) => {
    if (!rawValue) return null;
    const targetNorm = normalize(rawValue);

    // Candidatos para búsqueda laxa (con y sin %, con y sin decimales si son .00)
    const candidates = new Set([targetNorm]);

    // Si termina en %, agregar versión sin %
    if (targetNorm.endsWith("%")) {
      candidates.add(targetNorm.slice(0, -1));
    }

    // Agregar versión "numérica pura" si es posible parsear
    const numVal = parseFloat(targetNorm);
    if (!isNaN(numVal)) {
      candidates.add(String(numVal));      // "100"
      candidates.add(numVal.toFixed(2));   // "100.00"
    }

    for (const prod of Object.keys(analyticsData)) {
      const cats = analyticsData[prod] || {};
      for (const cat of Object.keys(cats)) {
        const rows = Array.isArray(cats[cat]) ? cats[cat] : [];
        for (let r = 0; r < rows.length; r++) {
          const row = rows[r];
          const entries = Object.entries(row).filter(([k]) => k !== "Nivel");
          for (const [colKey, v] of entries) {
            const cell = (v ?? "").toString().trim();
            const cellNorm = normalize(cell);

            // 1. Coincidencia directa normalizada
            if (candidates.has(cellNorm)) {
              return { prod, cat, rowIndex: r, colKey };
            }

            // 2. Coincidencia numérica laxa (ej: 100 vs 100.00)
            const cellNum = parseFloat(cellNorm);
            if (!isNaN(cellNum) && !isNaN(numVal)) {
              if (Math.abs(cellNum - numVal) < 0.01) { // Tolerancia de 0.01
                return { prod, cat, rowIndex: r, colKey };
              }
            }
          }
        }
      }
    }
    return null;
  };

  // Expone método imperativo para que Home pueda saltar a un valor de ESTA tabla
  useImperativeHandle(ref, () => ({
    jumpToValue: (rawValue) => {
      const loc = findLocationByValue(rawValue);
      if (!loc) return false;

      const { prod, cat, rowIndex, colKey } = loc;

      const runJump = () => {
        // Necesitamos asegurar que el DOM se haya actualizado si cambiamos de tab
        requestAnimationFrame(() => {
          setTimeout(() => { // Pequeño delay extra para asegurar renderizado
            const cellId = `${tableId}-cell-${sanitizeKey(prod)}-${sanitizeKey(cat)}-${rowIndex}-${sanitizeKey(colKey)}`;
            const el = document.getElementById(cellId);
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });

              // Reiniciar animación si ya tenía la clase
              el.classList.remove("flash-highlight");
              void el.offsetWidth; // Trigger reflow
              el.classList.add("flash-highlight");

              setTimeout(() => el.classList.remove("flash-highlight"), 2500);

              // Selección visual basada en data-col-key
              const headerSelector = `#${tableId}-head th[data-col-key]`;
              const ths = document.querySelectorAll(headerSelector);
              let colIndex = -1;
              ths.forEach((th, idx) => {
                // Usamos getAttribute para asegurar match exacto
                if (th.getAttribute("data-col-key") === colKey) colIndex = idx;
              });

              setCeldaSeleccionada({ fila: rowIndex, col: colIndex });
            } else {
              console.warn("Elemento no encontrado:", cellId);
            }
          }, 100);
        });
      };

      if (tabIdActivo !== prod) {
        setTabIdActivo(prod);
        // Al cambiar prod, el useEffect validará cat, pero lo forzamos aquí para inmediatez
        setTabCategoriaActiva(cat);
        setTimeout(runJump, 50);
      } else if (tabCategoriaActiva !== cat) {
        setTabCategoriaActiva(cat);
        setTimeout(runJump, 50);
      } else {
        runJump();
      }
      return true;
    }
  }));

  const exportTableToXLSX = (filename = "table.xlsx") => {
    if (!tabIdActivo || !tabCategoriaActiva) return;
    const tableData = analyticsData?.[tabIdActivo]?.[tabCategoriaActiva];
    if (!Array.isArray(tableData) || tableData.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(tableData);
    if (!ws["!ref"]) return;

    const range = XLSX.utils.decode_range(ws["!ref"]);
    const currencyRegex = /^\$\s?[\d,]+\.\d{2}$/;
    const unitsRegex = /^[\d,]+\.\d{2}$/;
    const percentRegex = /^\d+(\.\d+)?\s?%$/;

    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[cellAddress];
        if (cell && typeof cell.v === "string") {
          const value = cell.v.trim();
          if (currencyRegex.test(value)) {
            const numericValue = parseFloat(value.replace(/[\$,]/g, ""));
            cell.v = numericValue; cell.t = "n"; cell.z = "$#,##0.00";
          } else if (unitsRegex.test(value)) {
            const numericValue = parseFloat(value.replace(/,/g, ""));
            cell.v = numericValue; cell.t = "n"; cell.z = "#,##0.00";
          } else if (percentRegex.test(value)) {
            const numericValue = parseFloat(value.replace("%", "").trim()) / 100;
            cell.v = numericValue; cell.t = "n"; cell.z = "0.00%";
          }
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, filename);
  };

  const rows = (tabIdActivo && tabCategoriaActiva && analyticsData[tabIdActivo]?.[tabCategoriaActiva]) || [];
  const hasRows = Array.isArray(rows) && rows.length > 0;

  // FIX: calcular maxNivel UNA sola vez, no por cada fila (evita O(n²))
  const maxNivel = useMemo(() => {
    if (!hasRows) return 0;
    let max = 0;
    for (const row of rows) {
      const n = parseInt(row["Nivel"], 10);
      if (!isNaN(n) && n > max) max = n;
    }
    return max;
  }, [rows, hasRows]);

  if (!analyticsData || Productos.length === 0) return null;

  return (
    <div className="tabla-container">
      <div className="tabs-container">
        {/* Tabs padre */}
        <div className="tabs tabs-padre">
          {Productos.map((product, idx) => {
            const prodId = Array.isArray(analyticsIds) ? analyticsIds[idx] : undefined;
            return (
              <div
                key={product}
                className={`tab-btn ${tabIdActivo === product ? "active" : ""}`}
                title={prodId || ""}
                onClick={() => {
                  if (tabIdActivo !== product) setTabIdActivo(product);
                }}
              >
                <span className="tab-title">{product}</span>
                {prodId && <span className="tab-id">{prodId}</span>}
              </div>
            )
          })}
        </div>

        {/* Tabs hijo */}
        {tabIdActivo && (
          <div className="tabs tabs-hijo">
            {Object.keys(analyticsData[tabIdActivo] || {}).map((categoria) => (
              <div
                key={categoria}
                className={`tab-btn ${tabCategoriaActiva === categoria ? "active" : ""}`}
                onClick={() => { if (tabCategoriaActiva !== categoria) setTabCategoriaActiva(categoria); }}
              >
                {categoria}
              </div>
            ))}
          </div>
        )}

        {/* Tabla con scroll */}
        <div className="tabla-scroll">
          {hasRows ? (
            <table className="tabla-datos">
              <thead id={`${tableId}-head`}>
                <tr>
                  {Object.keys(rows[0])
                    .filter((colKey) => colKey !== "Nivel")
                    .map((colKey, colIndex) => (
                      <th
                        key={colKey}
                        data-col-key={colKey} // 👈 para localizar índice de columna de forma fiable
                        className={`sticky-col-${colIndex} ${celdaSeleccionada.col === colIndex ? "col-selected" : ""}`}
                      >
                        {colKey}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => {
                  const nivelValue = parseInt(row["Nivel"], 10);
                  const columnas = Object.entries(row).filter(([key]) => key !== "Nivel");

                  return (
                    <tr key={rowIndex} className={celdaSeleccionada.fila === rowIndex ? "row-selected" : ""}>
                      {columnas.map(([colKey, val], colIndex) => {
                        const cellId = `${tableId}-cell-${sanitizeKey(tabIdActivo)}-${sanitizeKey(tabCategoriaActiva)}-${rowIndex}-${sanitizeKey(colKey)}`;
                        return (
                          <td
                            id={cellId}
                            key={colIndex}
                            data-col-key={colKey}
                            className={`sticky-col-${colIndex} ${celdaSeleccionada.col === colIndex || celdaSeleccionada.fila === rowIndex
                                ? "cell-selected"
                                : ""
                              } ${colIndex === 0 && !isNaN(nivelValue) && nivelValue < maxNivel ? `col-nivel-${nivelValue}` : ""}`}
                            onClick={() => setCeldaSeleccionada({ fila: rowIndex, col: colIndex })}
                          >
                            {val}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p>No hay datos para mostrar en esta pestaña.</p>
          )}
        </div>

        {/* Botón de descargar */}
        <div className="excel-download">
          <button
            onClick={() => {
              const filename = `${tabIdActivo || "Reporte"}-${tabCategoriaActiva || "General"}.xlsx`;
              exportTableToXLSX(filename);
            }}
            className="excel-btn"
          >
            Excel
          </button>
        </div>
      </div>
    </div>
  );
});

export default DatosTabla;
